
import * as XLSX from 'xlsx';
import { importPromptsFromExcel, importPromptsFromMarkdown } from "@/services/promptImportService";
import { ImportResult } from "@/services/imports/importTypes";
import { toast } from "sonner";

export const handleExcelUpload = async (file: File, userId: string, setImporting: (value: boolean) => void, setImportStats: (stats: ImportResult | null) => void) => {
  setImporting(true);
  setImportStats(null);
  
  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(worksheet);
    
    if (!jsonData.length) {
      throw new Error('No data found in the Excel file');
    }
    
    const requiredFields = ['title', 'description', 'content', 'category_id'];
    const firstRow = jsonData[0] as any;
    
    const missingFields = requiredFields.filter(field => !(field in firstRow));
    if (missingFields.length > 0) {
      throw new Error(`Excel file is missing required fields: ${missingFields.join(', ')}`);
    }
    
    const result = await importPromptsFromExcel(jsonData as any[], userId);
    
    setImportStats(result);
    
    if (result.success) {
      toast(
        result.failed > 0 ? "Import partially successful" : "Import successful",
        {
          description: `${result.imported} prompts have been imported. ${result.failed} failed.`,
        }
      );
    } else {
      toast.error("Import failed", {
        description: `Failed to import prompts. ${result.errors[0]}`,
      });
    }
  } catch (error: any) {
    console.error('Error importing Excel file:', error);
    toast.error("Import failed", {
      description: error.message,
    });
  } finally {
    setImporting(false);
  }
};

export const handleMarkdownUpload = async (
  file: File, 
  userId: string,
  setImportingMarkdown: (value: boolean) => void, 
  setImportStats: (stats: ImportResult | null) => void,
  fetchPendingPrompts: () => void
) => {
  setImportingMarkdown(true);
  setImportStats(null);
  
  try {
    const content = await file.text();
    
    if (!content.trim()) {
      throw new Error('The Markdown file is empty');
    }
    
    const result = await importPromptsFromMarkdown(content, userId);
    
    setImportStats(result);
    
    if (result.success) {
      toast(
        result.failed > 0 ? "Import partially successful" : "Import successful",
        {
          description: `${result.imported} prompts have been imported and await your approval. ${result.failed} failed.`,
        }
      );
      
      fetchPendingPrompts();
    } else {
      toast.error("Import failed", {
        description: `Failed to import prompts. ${result.errors[0]}`,
      });
    }
  } catch (error: any) {
    console.error('Error importing Markdown file:', error);
    toast.error("Import failed", {
      description: error.message,
    });
  } finally {
    setImportingMarkdown(false);
  }
};

export const downloadTemplateFile = () => {
  const templateData = [
    {
      title: 'Example Prompt Title',
      description: 'A short description of what this prompt does',
      content: 'The actual prompt content goes here. Be detailed in your instructions.',
      category_id: 'productivity-hacks'
    }
  ];
  
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(templateData);
  
  XLSX.utils.sheet_add_aoa(ws, [
    ['This is a template for importing prompts. Fill out the following fields:'],
    ['title: The title of your prompt (required)'],
    ['description: A short description of what the prompt does (required)'],
    ['content: The actual prompt text (required)'],
    ['category_id: The category slug (required). Valid slugs are listed below'],
    [''],
    ['Valid category slugs:']
  ], { origin: 'A1' });
  
  XLSX.utils.book_append_sheet(wb, ws, 'Prompt Template');
  
  XLSX.writeFile(wb, 'prompt_import_template.xlsx');
};

export const downloadMarkdownTemplate = () => {
  const templateContent = `# Example Prompt Title

## Description
A short description of what this prompt does

## Prompt
The actual prompt content text goes here.
You can include multiple lines and formatting if needed.

## Category
productivity-hacks

## Source
reddit.com/r/prompt-engineering


# Another Example Prompt

## Description
This is another sample prompt to show how multiple prompts can be included

## Prompt Content
\`\`\`
This is a more complex prompt with code block formatting. 
You can use standard markdown features here.
\`\`\`

## Category
ideation

## Source
twitter.com/username
`;

  const blob = new Blob([templateContent], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'prompt_import_template.md';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
